import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setWalletOpen } from 'store/uiSlice';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import Home from 'pages/Home';

const DepositPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useLocalizedNavigate();
  const { isAuthenticated, isInitializing } = useAppSelector((state) => state.auth);
  const { isWalletOpen } = useAppSelector((state) => state.ui);

  const hasOpenedModalRef = useRef(false);

  useEffect(() => {
    if (isInitializing) return;

    // Requirement 4: If user is logged out, redirect immediately to home page (no modal)
    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    // Check if user came from an active game
    const savedGameData = sessionStorage.getItem('last_active_game');
    if (savedGameData) {
      try {
        const { game, state, timestamp } = JSON.parse(savedGameData);
        const isFresh = !timestamp || Date.now() - timestamp < 30 * 60 * 1000;
        if (game && isFresh) {
          // Requirement 1: Keep game opened in background and open wallet modal on top of it.
          // User closes modal and they are still in the game.
          navigate(`/game/casino/${game}`, { replace: true, state });
          dispatch(setWalletOpen(true));
          return;
        }
      } catch (e) {
        // ignore JSON parse errors
      }
    }

    // Requirement 3: If user logged in and in URL just writes /deposit, open wallet modal over Home
    if (!hasOpenedModalRef.current) {
      hasOpenedModalRef.current = true;
      dispatch(setWalletOpen(true));
    }
  }, [isAuthenticated, isInitializing, dispatch, navigate]);

  // When user arrived directly on /deposit (not from a game) and closes the modal, navigate to Home
  useEffect(() => {
    if (hasOpenedModalRef.current && !isWalletOpen) {
      hasOpenedModalRef.current = false;
      navigate('/');
    }
  }, [isWalletOpen, navigate]);

  // While checking auth, redirecting logged-out user, or redirecting to game, don't flash content
  const savedGameData = sessionStorage.getItem('last_active_game');
  let hasActiveGame = false;
  if (savedGameData) {
    try {
      const { game, timestamp } = JSON.parse(savedGameData);
      if (game && (!timestamp || Date.now() - timestamp < 30 * 60 * 1000)) {
        hasActiveGame = true;
      }
    } catch (_error) {
      // ignore JSON parse errors
    }
  }

  if ((!isAuthenticated && !isInitializing) || hasActiveGame) {
    return null;
  }

  return <Home />;
};

export default DepositPage;
